import classNames from "classnames";
import dayjs from "dayjs";
import { useState } from "react";
import { Button } from "../../../components/buttons/button";
import Dropdown from "../../../components/dropdown/dropdown";
import { EmptyState } from "../../../components/empty";
import {
  FindEventCard,
  FindEventCardLoader,
} from "../../../components/event/findEventCard";
import {
  TrendingCardLoader,
  TrendingEventCard,
} from "../../../components/event/trendingEventCard";
import { CaretDown } from "../../../components/icon/downcaret";
import { Icon } from "../../../components/icon/icon";
import { SearchInput } from "../../../components/inputs/searchInput";
import { FilterPill } from "../../../components/pill/filterPill";
import { TabNav } from "../../../components/tabNav";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useDates } from "../../../customeHooks/useDates";
import { useMonthlyDates } from "../../../customeHooks/useMonthlyDates";
import { useWeeklyDates } from "../../../customeHooks/useWeeklyDates";
import {
  useFetchAvailableCategoriesQuery,
  useFindEventsQuery,
} from "../../../redux/features/event/event.api";
import {
  EventCategories,
  FindEventsQuery,
} from "../../../redux/features/event/event.types";
import { formatDate, formatToNaira } from "../../../utils/helpers";

const categoryOptions: {
  icon: string;
  iconClassName: string;
  text: string;
  value?: "free" | EventCategories;
}[] = [
  {
    icon: "calendar",
    iconClassName: "bg-[#49CA83]",
    text: "All",
    value: undefined,
  },
  {
    icon: "sparkle",
    iconClassName: "bg-[#FC800C]",
    text: "Free",
    value: "free",
  },
  {
    icon: "music",
    iconClassName: "bg-[#9939FF]",
    text: "Concerts",
    value: "Concert",
  },
  {
    icon: "party-popper",
    iconClassName: "bg-[#3988FF]",
    text: "Parties",
    value: "Party",
  },
  {
    icon: "hand-platter",
    iconClassName: "bg-[#FF3838]",
    text: "Foodie",
    value: "Food & Drinks",
  },
  {
    icon: "briefcase",
    iconClassName: "bg-[#881414]",
    text: "Career",
    value: "Career",
  },
];

const findEventLimit = 10;

export const FindEventsPage = () => {
  const [query, setQuery] = useState<FindEventsQuery>({
    page: 1,
    limit: findEventLimit,
    search: undefined,
    sortOrder: "asc",
    location: undefined,
    category: undefined,
    price: undefined,
    date: undefined,
    startDate: undefined,
    endDate: undefined,
  });

  const priceFilters = [
    {
      name: `${formatToNaira(5000)}+`,
      value: "5000-10999",
      onClick: () => {
        setQuery({
          ...query,
          category: undefined,
          page: 1,
          price: "5000-10999",
        });
      },
    },
    {
      name: `${formatToNaira(10000)}+`,
      value: "11000-25000",
      onClick: () => {
        setQuery({
          ...query,
          category: undefined,
          page: 1,
          price: "11000-25000",
        });
      },
    },
    {
      name: `${formatToNaira(25000)}+`,
      value: "25000+",
      onClick: () => {
        setQuery({
          ...query,
          category: undefined,
          page: 1,
          price: "25000+",
        });
      },
    },
  ];

  const { data: events, isFetching: fetchingEvents } = useFindEventsQuery(
    {
      ...query,
      search: query.search === "" ? undefined : query.search,
    },
    { refetchOnMountOrArgChange: true },
  );

  const { data: trendingEvents, isFetching: fetchingTrendingEvents } =
    useFindEventsQuery(
      {
        page: 1,
        limit: 5,
        sortOrder: "desc",
      },
      { refetchOnMountOrArgChange: true },
    );

  const { data: availableCatefories, isFetching: fetchingAvailableCategories } =
    useFetchAvailableCategoriesQuery(
      {},
      {
        refetchOnMountOrArgChange: true,
      },
    );

  return (
    <div className="max-width-website relative flex w-full flex-col gap-6 py-3 md:py-9">
      {!["", 0, undefined].includes(trendingEvents?.data?.data?.length) && (
        <div
          className={`relative !bg-contain !bg-bottom !bg-no-repeat`}
          style={{
            background: `url("/assets/images/gradientBg.png")`,
          }}
        >
          <div
            className="relative flex w-full items-center justify-between gap-4"
            style={{}}
          >
            <p className="text-24 font-unbounded text-grey-800">Trending 🔥</p>

            <Button
              type="button"
              text="Lagos"
              icon="map-pin"
              variation="plain-text-grey"
              className="max-w-fit"
              disabled
            />
          </div>

          <div className="overflow-x-scroll py-6">
            <div className="relative grid w-max auto-cols-max grid-flow-col gap-[20px]">
              {fetchingTrendingEvents ? (
                <>
                  {[1, 2, 3, 4, 5]?.map((idx) => {
                    return <TrendingCardLoader key={`${idx}`} />;
                  })}
                </>
              ) : undefined}

              {!fetchingTrendingEvents &&
              !["", 0, undefined].includes(
                trendingEvents?.data?.data?.length,
              ) ? (
                <>
                  {trendingEvents?.data?.data
                    ?.slice(0, 5)
                    ?.map((event, idx) => {
                      return (
                        <TrendingEventCard
                          event={event}
                          key={`${event._id}-${idx}`}
                        />
                      );
                    })}
                </>
              ) : undefined}
            </div>
          </div>
        </div>
      )}

      <div className="">
        <div className="flex w-full items-center justify-between gap-4">
          <p className="text-24 font-unbounded text-grey-800">All Events</p>

          <SearchInput
            placeholder="Search"
            searchFunction={(searchVal) => {
              setQuery({
                ...query,
                page: 1,
                search: searchVal,
                limit: findEventLimit,
              });
            }}
            inputClassName="!rounded-full !bg-white"
            iconClassName="text-grey-600"
            className="!max-w-[300px]"
            debouncedSearch={false}
            customElement={
              <Icon
                svg="arrow-right"
                width={24}
                height={24}
                className="cursor-pointer rounded-full bg-primary p-[5px] text-white"
              />
            }
          />
        </div>

        <div className="mt-6 flex flex-row flex-wrap items-center justify-between gap-4 md:mt-8">
          <div className="flex flex-row gap-[10px] overflow-x-scroll">
            {(!query.price || query.price === "free") &&
              !query.date &&
              !query.startDate &&
              !query.endDate && (
                <>
                  {categoryOptions
                    .filter(
                      (c) =>
                        c.value === "free" ||
                        c.value === undefined ||
                        (fetchingAvailableCategories
                          ? true
                          : availableCatefories?.data?.includes(c.value!)),
                    )
                    .map((cat, idx) => {
                      return (
                        <BgWrapper
                          loading={fetchingAvailableCategories}
                          loaderHeight={20}
                          key={`category-${idx}`}
                          className={classNames(
                            "soft-shrink flex items-center gap-2 !px-3 !py-[10px]",
                            "cursor-pointer text-sm font-bold",
                            {
                              "!w-[70px]": fetchingAvailableCategories,
                              "!border-[2px] !border-primary":
                                ((query.category === cat.value &&
                                  query.price === undefined) ||
                                  (query.price === cat.value &&
                                    query.category === undefined)) &&
                                !fetchingAvailableCategories,
                            },
                          )}
                          onClick={() => {
                            if (cat.value === "free") {
                              setQuery({
                                ...query,
                                page: 1,
                                limit: findEventLimit,
                                category: undefined,
                                price: "free",
                                date: undefined,
                                startDate: undefined,
                                endDate: undefined,
                              });
                            } else {
                              setQuery({
                                ...query,
                                page: 1,
                                limit: findEventLimit,
                                category: cat.value,
                                price: undefined,
                                date: undefined,
                                startDate: undefined,
                                endDate: undefined,
                              });
                            }
                          }}
                        >
                          <Icon
                            svg={cat.icon}
                            className={`rounded-full p-[5px] text-white ${cat.iconClassName}`}
                            width={24}
                            height={24}
                          />
                          <p>{cat.text}</p>
                        </BgWrapper>
                      );
                    })}
                </>
              )}

            {query.price && query.price !== "free" ? (
              <FilterPill
                text={
                  priceFilters.find((pf) => pf.value === query.price)?.name!
                }
                onCancel={() => {
                  setQuery({
                    ...query,
                    price: undefined,
                    page: 1,
                  });
                }}
              />
            ) : undefined}

            {query.date ? (
              <FilterPill
                text={query?.date?.replace("-", " ") ?? ""}
                onCancel={() => {
                  setQuery({
                    ...query,
                    date: undefined,
                    startDate: undefined,
                    endDate: undefined,
                    page: 1,
                  });
                }}
                className="!capitalize"
              />
            ) : undefined}

            {query.startDate && query.endDate ? (
              <FilterPill
                text={`${formatDate(query?.startDate, "MMM Do")} - ${formatDate(query?.endDate, "MMM Do")}`}
                onCancel={() => {
                  setQuery({
                    ...query,
                    date: undefined,
                    startDate: undefined,
                    endDate: undefined,
                    page: 1,
                  });
                }}
                className=""
              />
            ) : undefined}

            {(query.price && query.price !== "free") ||
            query.date ||
            (query.startDate && query.endDate) ? (
              <button
                className="w-fit text-base font-semibold text-grey-800"
                type="button"
                onClick={() => {
                  setQuery({
                    ...query,
                    price: undefined,
                    page: 1,
                    search: undefined,
                    date: undefined,
                    startDate: undefined,
                    endDate: undefined,
                  });
                }}
              >
                Reset all
              </button>
            ) : undefined}
          </div>

          <div className="flex w-full items-center gap-[10px] md:w-fit">
            <p className="flex-1 text-base font-semibold text-grey-800">
              Filter by
            </p>

            <Dropdown
              menuItems={priceFilters}
              menuClassName="!min-w-[177px]"
              menuItemsClassName="!text-grey-950 !font-bold"
              position="bottom end"
            >
              <Button
                type="button"
                text={`Price`}
                variation="white-text-grey"
                size="small"
                extra={<CaretDown width={20} height={20} />}
                className=""
              />
            </Dropdown>

            <DateFilter query={query} setQuery={setQuery} />
          </div>
        </div>

        {!fetchingEvents &&
        query.search &&
        (events?.data?.totalCount ?? 0) > 0 ? (
          <div className="mt-4">
            <p className="text-base font-semibold text-grey-800">
              {events?.data?.totalCount}{" "}
              {events?.data?.totalCount === 1 ? "event" : "events"} found for '
              {query.search}'
            </p>
          </div>
        ) : undefined}

        <div className="mt-6 grid grid-cols-1 gap-10 md:mt-14 md:grid-cols-2">
          {!(fetchingEvents && query.limit! === 5) &&
          !["", 0, undefined].includes(events?.data?.data?.length) ? (
            <>
              {events?.data?.data?.map((event, idx) => {
                return (
                  <FindEventCard event={event} key={`${event._id}-${idx}`} />
                );
              })}
            </>
          ) : undefined}

          {fetchingEvents ? (
            <>
              {[1, 2, 3, 4]?.map((idx) => {
                return <FindEventCardLoader key={`${idx}`} />;
              })}
            </>
          ) : undefined}
        </div>

        <div
          className={classNames(
            "mt-14 flex w-full items-center justify-center",
            {
              hidden:
                events?.data?.data?.length === events?.data?.totalCount ||
                ["", 0, undefined].includes(events?.data?.data?.length) ||
                fetchingEvents,
            },
          )}
        >
          <Button
            type="button"
            text="Show More Events"
            variation="plain-grey"
            loading={fetchingEvents}
            onClick={() => {
              setQuery({
                ...query,
                limit: query.limit! + findEventLimit,
              });
            }}
            className="max-w-fit"
          />
        </div>

        {!fetchingEvents &&
        ["", 0, undefined].includes(events?.data?.data?.length) ? (
          <>
            <EmptyState
              text="No Events Found"
              subtext="There are no events that meet your seach"
              className="w-full !bg-transparent"
              image="/assets/images/search-glass.png"
            />
          </>
        ) : undefined}
      </div>
    </div>
  );
};

const DateFilter = ({
  query,
  setQuery,
}: {
  query: FindEventsQuery;
  setQuery: (q: FindEventsQuery) => void;
}) => {
  const [view, setView] = useState<"custom" | "default">("default");
  const [custView, setCustView] = useState<"months" | "weeks">("weeks");
  // const [custDates, setCustDates] = useState({
  //   startDate: "",
  //   endDate: "",
  // });

  const { tomorrow, saturday, sunday } = useDates();

  const {
    currentMonthRangeLabel,
    canGoBack,
    canGoNext,
    weekRanges,
    goToNextWeeks,
    goToPreviousWeeks,
  } = useWeeklyDates({
    initialDate: dayjs(),
  });

  const { months, selectedMonth, setSelectedMonth } = useMonthlyDates({
    initialDate: dayjs(),
  });

  const defaultOptions = [
    {
      text: <p className="font-bold text-primary">Today</p>,
      onClick: () => {
        setQuery({
          ...query,
          page: 1,
          date: "today",
          startDate: undefined,
          endDate: undefined,
          search: undefined,
        });
      },
      closeOnClick: true,
    },
    {
      text: (
        <p className="flex flex-col text-sm">
          <span className="font-bold text-primary">Tomorrow</span>
          <span className="text-grey-500">
            {formatDate(tomorrow, "MMM Do")}
          </span>
        </p>
      ),
      onClick: () => {
        setQuery({
          ...query,
          page: 1,
          date: "tomorrow",
          startDate: undefined,
          endDate: undefined,
          search: undefined,
        });
      },
      closeOnClick: true,
    },
    {
      text: (
        <p className="flex flex-col text-sm">
          <span className="font-bold text-primary">This Weekend</span>
          <span className="text-grey-500">
            {formatDate(saturday, "MMM Do")} - {formatDate(sunday, "MMM Do")}
          </span>
        </p>
      ),
      onClick: () => {
        setQuery({
          ...query,
          page: 1,
          date: "this-weekend",
          startDate: undefined,
          endDate: undefined,
          search: undefined,
        });
      },
      closeOnClick: true,
    },
    {
      text: <p className="font-bold text-primary">Choose Date</p>,
      onClick: () => {
        setView("custom");
      },
      closeOnClick: false,
    },
  ];

  return (
    <Dropdown
      customMenuItems={({ close }) => {
        return (
          <div
            className={classNames(
              "rounded-xl border border-grey-200 !bg-white",
              "h-full overflow-y-scroll",
              {
                "flex w-[177px] flex-col": view === "default",
                "!max-h-[250px] w-[300px] md:!max-h-fit": view === "custom",
              },
            )}
          >
            {view === "default" ? (
              <>
                {defaultOptions.map((option, idx) => {
                  return (
                    <button
                      type="button"
                      onClick={() => {
                        option.onClick();
                        if (option.closeOnClick) {
                          close();
                        }
                      }}
                      className="w-full border-b border-b-grey-200 p-4 text-left last:border-b-0"
                      key={`option-${idx}`}
                    >
                      {option.text}
                    </button>
                  );
                })}
              </>
            ) : (
              <>
                <div className="px-3 py-4">
                  <TabNav
                    activeTab={custView}
                    navs={[
                      {
                        title: "Weeks",
                        to: "weeks",
                        onClick: () => {
                          setCustView("weeks");
                        },
                      },
                      {
                        title: "Months",
                        to: "months",
                        onClick: () => {
                          setCustView("months");
                        },
                      },
                    ]}
                    variant="switcher"
                  />
                </div>

                <div className="flex items-center border-y border-grey-200 p-3">
                  <Icon
                    svg="caret-down"
                    width={20}
                    height={20}
                    className={classNames("rotate-90", {
                      "cursor-not-allowed text-grey-200": !canGoBack,
                      "cursor-pointer text-grey-500": canGoBack,
                      hidden: custView === "months",
                    })}
                    onClick={() => {
                      goToPreviousWeeks();
                    }}
                  />

                  <p className="flex-1 text-center text-base font-bold text-primary">
                    {custView === "weeks"
                      ? currentMonthRangeLabel
                      : dayjs().year()}
                  </p>

                  <Icon
                    svg="caret-down"
                    width={20}
                    height={20}
                    className={classNames("-rotate-90", {
                      "cursor-not-allowed text-grey-200": !canGoNext,
                      "cursor-pointer text-grey-500": canGoNext,
                      hidden: custView === "months",
                    })}
                    onClick={() => {
                      goToNextWeeks();
                    }}
                  />
                </div>

                {custView === "weeks" ? (
                  <div className="flex flex-col">
                    {weekRanges.map((wRange, idx) => {
                      return (
                        <button
                          type="button"
                          className={classNames(
                            "px-5 py-3 text-left text-base font-bold",
                            {
                              "text-primary": !wRange.disabled,
                              "cursor-not-allowed text-grey-300":
                                wRange.disabled,
                            },
                          )}
                          onClick={() => {
                            setQuery({
                              ...query,
                              page: 1,
                              date: undefined,
                              startDate: wRange.start.format("YYYY-MM-DD"),
                              endDate: wRange.end.format("YYYY-MM-DD"),
                              search: undefined,
                            });
                            setSelectedMonth(null);
                            close();
                          }}
                          disabled={wRange.disabled}
                          key={`week-range-${idx}`}
                        >
                          {wRange.label}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-y-6 py-3">
                    {months.map((month, idx) => {
                      return (
                        <button
                          type="button"
                          className={classNames(
                            "w-full rounded-[4px] p-2 text-base font-bold",
                            {
                              "text-primary": !month.disabled,
                              "cursor-not-allowed text-grey-300":
                                month.disabled,
                              "bg-grey-100":
                                selectedMonth === month &&
                                !!query.startDate &&
                                !!query.endDate,
                            },
                          )}
                          onClick={() => {
                            setQuery({
                              ...query,
                              page: 1,
                              date: undefined,
                              startDate: month.start.format("YYYY-MM-DD"),
                              endDate: month.end.format("YYYY-MM-DD"),
                              search: undefined,
                            });
                            setSelectedMonth(month);
                            close();
                          }}
                          disabled={month.disabled}
                          key={`week-range-${idx}`}
                        >
                          {month.label}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* <div className="border-t border-grey-200 p-3">
                  <Button
                    type="button"
                    variation="primary"
                    text="Apply"
                    onClick={() => {
                      
                    }}
                  />
                </div> */}
              </>
            )}
          </div>
        );
      }}
      menuClassName="!min-w-fit"
      menuItemsClassName="!text-grey-950 !font-bold"
      position="bottom end"
      closeOnClick={false}
      onMenuClose={() => {
        setView("default");
      }}
    >
      <Button
        type="button"
        text={`Date`}
        variation="white-text-grey"
        size="small"
        extra={<CaretDown width={20} height={20} />}
        className=""
      />
    </Dropdown>
  );
};
